import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { Modal } from './Modal';
import './Button.css';

/**
 * Diálogo de confirmação e avisos rápidos (toasts) do painel (auditoria: design system, itens 5 e 7).
 *
 * Regra de uso:
 * - `confirmar(...)` antes de toda ação que não dá para desfazer (excluir, remover, trocar papel).
 *   Para ações destrutivas, a mensagem cita o nome do item.
 * - `avisar(...)` para o resultado de uma ação (sucesso ou erro) que não precisa de resposta.
 * - Avisos que ficam na tela (ex.: certificado vencendo) continuam como faixa na própria página.
 */
export interface OpcoesDeConfirmacao {
  titulo: string;
  mensagem?: ReactNode;
  /** Texto do botão que confirma (padrão "Confirmar"). */
  confirmar?: string;
  cancelar?: string;
  /** Botão vermelho, para ações destrutivas. */
  perigo?: boolean;
}

type TipoDeAviso = 'sucesso' | 'erro' | 'info';
interface Aviso { id: number; texto: string; tipo: TipoDeAviso }

interface FeedbackContexto {
  confirmar: (opcoes: OpcoesDeConfirmacao) => Promise<boolean>;
  avisar: (texto: string, tipo?: TipoDeAviso) => void;
}

const Contexto = createContext<FeedbackContexto | null>(null);

const DURACAO_DO_AVISO_MS = 5000;

export function FeedbackProvider({ children }: { children: ReactNode }) {
  const [pedido, setPedido] = useState<(OpcoesDeConfirmacao & { responder: (sim: boolean) => void }) | null>(null);
  const [avisos, setAvisos] = useState<Aviso[]>([]);
  const proximoId = useRef(1);

  const confirmar = useCallback((opcoes: OpcoesDeConfirmacao) => {
    return new Promise<boolean>((resolve) => {
      setPedido({ ...opcoes, responder: resolve });
    });
  }, []);

  const responder = (sim: boolean) => {
    pedido?.responder(sim);
    setPedido(null);
  };

  const fecharAviso = useCallback((id: number) => {
    setAvisos((atuais) => atuais.filter((a) => a.id !== id));
  }, []);

  const avisar = useCallback((texto: string, tipo: TipoDeAviso = 'info') => {
    const id = proximoId.current++;
    setAvisos((atuais) => [...atuais.slice(-2), { id, texto, tipo }]);
    setTimeout(() => fecharAviso(id), DURACAO_DO_AVISO_MS);
  }, [fecharAviso]);

  return (
    <Contexto.Provider value={{ confirmar, avisar }}>
      {children}

      {pedido && (
        <Modal pedirParaFechar={() => responder(false)} rotuloId="confirmacao-titulo" zIndex={1100}>
          <div style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border-default)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-6)', width: '100%', maxWidth: '440px', display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)' }}>
              {pedido.perigo && <AlertTriangle size={22} style={{ color: 'var(--color-error)', flexShrink: 0, marginTop: '2px' }} />}
              <h2 id="confirmacao-titulo" style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-semibold)', margin: 0, overflowWrap: 'anywhere' }}>{pedido.titulo}</h2>
            </div>
            {pedido.mensagem && (
              <div style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-sm)', lineHeight: 1.6, overflowWrap: 'anywhere' }}>{pedido.mensagem}</div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
              <button type="button" className="btn btn--outline" onClick={() => responder(false)}>
                {pedido.cancelar ?? 'Cancelar'}
              </button>
              <button type="button" className={`btn ${pedido.perigo ? 'btn--danger' : 'btn--primary'}`} onClick={() => responder(true)}>
                {pedido.confirmar ?? 'Confirmar'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Avisos rápidos: canto inferior direito; no celular, ocupam a largura com margem. */}
      <div aria-live="polite" style={{ position: 'fixed', bottom: 'var(--space-4)', right: 'var(--space-4)', left: 'auto', zIndex: 1200, display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', maxWidth: 'min(420px, calc(100vw - 2 * var(--space-4)))', pointerEvents: 'none' }}>
        {avisos.map((aviso) => {
          const Icone = aviso.tipo === 'sucesso' ? CheckCircle2 : aviso.tipo === 'erro' ? XCircle : Info;
          const cor = aviso.tipo === 'sucesso' ? 'var(--color-success)' : aviso.tipo === 'erro' ? 'var(--color-error)' : 'var(--color-info)';
          return (
            <div key={aviso.id} role={aviso.tipo === 'erro' ? 'alert' : 'status'} className="animate-fade-in-up" style={{ pointerEvents: 'auto', display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)', padding: 'var(--space-3) var(--space-4)', background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border-strong)', borderLeft: `3px solid ${cor}`, borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-lg)', fontSize: 'var(--text-sm)', color: 'var(--color-text-primary)' }}>
              <Icone size={18} style={{ color: cor, flexShrink: 0, marginTop: '2px' }} />
              <span style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{aviso.texto}</span>
              <button type="button" onClick={() => fecharAviso(aviso.id)} aria-label="Fechar aviso" style={{ color: 'var(--color-text-secondary)', display: 'inline-flex', padding: '2px', flexShrink: 0 }}>
                <X size={16} />
              </button>
            </div>
          );
        })}
      </div>
    </Contexto.Provider>
  );
}

/** Sem o provider (ex.: em teste isolado), cai no confirm/alert do navegador. */
export function useFeedback(): FeedbackContexto {
  const contexto = useContext(Contexto);
  if (contexto) return contexto;
  return {
    confirmar: async (o) => window.confirm([o.titulo, typeof o.mensagem === 'string' ? o.mensagem : ''].filter(Boolean).join('\n\n')),
    avisar: (texto) => window.alert(texto),
  };
}
